import { useEffect, useState } from "react";
import { Dialog } from "@headlessui/react";
import { FormBox, ModalFoterBox, ModalHeaderBox } from "../../components/box/Box";
import Button from "../../components/button/Button";
import { Modal, ModalProp } from "../../components/modal/Modal"
import { useForm, Controller } from "react-hook-form";
import { RadioButton, RadioButtonItem } from "../../components/radiobutton/RadioButton";
import { useInput } from "../../hooks/useInput";
import FormController from "../../components/fields/FormController";
import { SelectFields, SelectItem } from "../../components/select/SelectFields";
import { useDispatch } from "react-redux";
import { AlertValue, alertOpen } from "../../features/alert.slice";
import { ConstMessage } from "../../config/const";
import { 
  PointCategory, 
  PointType,
  PointAdjustHistory,
  pointAdjustSchema,
  newPointAdjustHistory,
  PtAwardedSettings,
} from "../../model/PointData";
import { yupResolver } from "@hookform/resolvers/yup";
import { TextField } from "../../components/fields/Fields";

export type UserPointAdjustmentModalProps = {
  pointCategoryList: PointCategory[];
  pointTypeList: PointType[];
  onClickConfirm: (data: any) => Promise<void>;
  ptAwardedSettingsList: PtAwardedSettings[];
}

export const UserPointAdjustmentModal = ({
  open,
  toggle,
  closeOnClickOutside,
  pointCategoryList,
  pointTypeList,
  onClickConfirm,
  ptAwardedSettingsList,
}: ModalProp & UserPointAdjustmentModalProps) => {

  const dispatch = useDispatch();

  const { control, handleSubmit, reset, setValue } = 
    useForm<PointAdjustHistory & {ptTypeFlg: boolean}>(
      { resolver: yupResolver(pointAdjustSchema), defaultValues: newPointAdjustHistory()}
    );

  const [selectCategoryList, setSelectCategoryList] = useState<RadioButtonItem[]>([]);
  const [selectTypeList, setSelectTypeList] = useState<SelectItem[]>([]);
  const [formData, setFormData] = useState<PointAdjustHistory>(newPointAdjustHistory);

  const pointcategory = useInput("0");

  useEffect(() => {
    const f = async () => {
      try {
        // ラジオボタン用のポイントカテゴリデータ
        const selectCategoriesItem = pointCategoryList.map((item) => ({id: String(item.categoryId), title: item.categoryName}));
        setSelectCategoryList(selectCategoriesItem);
        // ボランティアポイントのポイントタイプをセレクトボックスの値に設定する
        const selectType = pointTypeList.filter((item) => item.categoryId === 0);
        let selectTypeItem = [{id: "", name: ""}];
        selectType.map((item) => (selectTypeItem.push({id: item.typeId, name: item.typeName})));
        setSelectTypeList(selectTypeItem);
      } catch (error) {
        console.error(error);
        dispatch(alertOpen(AlertValue(ConstMessage.ERR_FAILED_GET_DATA, "danger")));
      }
    };
    f();
  },[]);

  // 登録後のフォームを初期化
  useEffect(() => {
    if(!open){
      reset();
      pointcategory.setValue("0");
    }
  },[open]);

  // ラジオボタンを押下した時のポイントタイプを設定
  useEffect(() => {
    try{
      const selectType = pointTypeList?.filter((item) => item.categoryId === Number(pointcategory.value));
      let selectTypeItem = [{id: "", name: ""}];
      selectType?.map((item) => (selectTypeItem.push({id: item.typeId, name: item.typeName})));
      setSelectTypeList(selectTypeItem);
      setValue('ptCategoryId', pointcategory.value);
      setValue('ptTypeId', selectTypeItem[0].id);
    }catch(err){
      console.error(err);
    }
  },[pointcategory.value]);

  const onPointTypeSelect = (ptTypeId: string) => {
    setFormData({...formData, ptTypeId: ptTypeId});
    // ポイント設定に登録されているポイントタイプか確認
    const result = ptAwardedSettingsList.filter((item) => item.typeId === ptTypeId);
    if(result && result.length > 0){
      setValue("ptTypeFlg", false);
    }else{
      // ポイント設定がされていない
      setValue("ptTypeFlg", true);
    }
  };

  return (
    <Modal
      open={open}
      toggle={toggle}
      closeOnClickOutside={closeOnClickOutside}
    >
      <div className="overflow-x-auto">
        <Dialog.Panel className="transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all w-[calc(100vw_-_25rem)] min-w-[610px] max-w-[620px]">
          <form
            className=""
            onSubmit={handleSubmit(onClickConfirm)}
          >
            <div className="w-full px-2 pb-4 sm:p-4">
              <ModalHeaderBox title={"ポイント調整"} />
              <div className="w-full mt-3">
                {selectCategoryList && selectCategoryList.length > 0 ? 
                  <>
                    <RadioButton
                      className={"sm:self-center"}
                      label={"カテゴリ"}
                      children={undefined}
                      value={pointcategory}
                      name={"ptCategoryId"}
                      radioButtonItem={selectCategoryList}
                      required
                    />
                  </> : <></>
                }
                {selectTypeList && selectTypeList.length > 0 ? 
                  <>
                    <Controller
                      name={"ptTypeId"}
                      control={control}
                      render={({ field, fieldState }) => (
                      <SelectFields
                        id="ptTypeId"
                        color="indigo"
                        label={"タイプ"}
                        className={"w-2/5 mt-6 h-20"}
                        selectClassName={"h-[39px]"}
                        selectItems={selectTypeList}
                        onChange={(e: any) => {onPointTypeSelect(e.target.value);field.onChange(e)}}
                        required
                        error={fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                    />
                  </> : <></>
                }
                <FormBox gridcol={"grid-cols-1"} className={"h-24"}>
                  <FormController
                    control={control}
                    name={"ptName"}
                    label={"ポイント名"}
                    type={"text"}
                    style={{ 'width': '37rem' }}
                    maxLength={40}
                    required
                  />
                </FormBox>
                <div className="grid grid-cols-2 h-24 w-full pt-5">
                  <FormController
                    control={control}
                    name={"occurrenceDate"}
                    label={"ポイント発生日"}
                    type={"date"}
                    className={"w-40"}
                    required
                  />
                  <Controller
                    name={"grantPoints"}
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        type={"text"}
                        label={"ポイント数"}
                        required
                        error={fieldState.error}
                        helperText={fieldState.error?.message}
                        style={{ 'width': '7.5rem', 'text-align': 'right' }}
                        maxLength={formData.grantPoints && String(formData.grantPoints).slice(0, 1) === "-" ? 6 : 5}
                        onChange={(e: any) => {
                          setFormData({...formData, grantPoints: e.target.value});
                          setValue('grantPoints', e.target.value);
                          field.onChange(e);
                        }}
                      />
                    )}
                  />
                </div>
                <FormBox gridcol="grid-cols-1" className={"w-[37rem] mb-5"}>
                  <FormController
                    control={control}
                    name={"note"}
                    label={"備考"}
                    type={"textarea"}
                    className={"h-[80px]"}
                  />
                </FormBox>
              </div>
            </div>
            <ModalFoterBox>
              <Button
                className={"w-[132px]"}
                outline
                onClick={() => {
                  reset();
                  toggle(true);
                }}
                children={"キャンセル"}
              />
              <Button
                className={"ml-2 w-[132px]"}
                submit
                children={"確認"}
              />
            </ModalFoterBox>
          </form>
        </Dialog.Panel>
      </div>
    </Modal>
  )
}